import * as React from 'react';
import { Badge as ChakraBadge, IconButton, Box } from '@chakra-ui/react';
import { LuX } from 'react-icons/lu';

/**
 * Valid color palette options for Badge component
 */
export type BadgeColorPalette =
  | 'gray'
  | 'brand'
  | 'green'
  | 'yellow'
  | 'red'
  | 'blue'
  | 'orange'
  | 'purple';

export interface BadgeProps {
  variant?: 'solid' | 'subtle' | 'outline';
  colorPalette?: BadgeColorPalette;
  size?: 'sm' | 'md' | 'lg';
  dot?: boolean;
  dotColor?: string;
  icon?: React.ReactNode;
  onRemove?: () => void;
  children: React.ReactNode;
}

// Map TMS status to color palettes
const statusColorMap: Record<string, string> = {
  pending: 'yellow',
  'in-progress': 'blue',
  completed: 'green',
  cancelled: 'red',
  delayed: 'orange',
};

const Badge = React.forwardRef<HTMLDivElement, BadgeProps>(
  (
    {
      variant = 'subtle',
      colorPalette = 'gray',
      size = 'md',
      dot,
      dotColor,
      icon,
      onRemove,
      children,
      ...restProps
    },
    ref
  ) => {
    // Filter out Badge-specific props from DOM props
    const domProps = Object.fromEntries(
      Object.entries(restProps).filter(
        ([key]) =>
          !['variant', 'colorPalette', 'size', 'dot', 'dotColor', 'icon', 'onRemove'].includes(key)
      )
    );

    // Determine dot color based on colorPalette if not provided
    const getDotColor = () => {
      if (dotColor) return dotColor;

      const colorMap: Record<string, string> = {
        brand: '#0066CC',
        green: '#28A745',
        yellow: '#FFC107',
        red: '#DC3545',
        blue: '#17A2B8',
        orange: '#FF6B35',
        purple: '#6B46C1',
        gray: '#6C757D',
      };

      return colorMap[colorPalette] || '#6C757D';
    };

    return (
      <Box as="span" display="inline-flex" alignItems="center" gap={1} ref={ref} {...domProps}>
        <ChakraBadge
          variant={variant}
          colorPalette={colorPalette}
          size={size}
          display="inline-flex"
          alignItems="center"
        >
          {/* Dot indicator */}
          {dot && (
            <Box
              as="span"
              width={2}
              height={2}
              borderRadius="full"
              backgroundColor={getDotColor()}
              marginInlineEnd={1.5}
              aria-hidden="true"
            />
          )}

          {/* Icon */}
          {icon && (
            <Box as="span" marginInlineEnd={1} display="inline-flex" aria-hidden="true">
              {icon}
            </Box>
          )}

          {/* Content */}
          {children}
        </ChakraBadge>

        {/* Remove button */}
        {onRemove && (
          <IconButton
            aria-label="Remove"
            size="xs"
            variant="ghost"
            onClick={onRemove}
            marginInlineStart={1}
          >
            <LuX />
          </IconButton>
        )}
      </Box>
    );
  }
);

Badge.displayName = 'Badge';

// Export helper to get color palette from status
export const getStatusColorPalette = (status: string): string => {
  return statusColorMap[status] || 'gray';
};

export { Badge };
